import { Alert, Box, Button, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { FC, ReactNode, useState } from 'react';
import authProvider from '../../../authProvider';

interface AppErrorBoundaryProps {
  children: ReactNode;
  /** Shown instead of the generic copy, to name what stopped working. */
  scope?: string;
}

/**
 * Signs out through `authProvider.logout`, the same path as every other sign-out,
 * so a Hub-minted session is also ended on the Hub and the session monitor is
 * reset. Imported directly rather than through `useLogout`, since one of the two
 * boundaries is mounted outside `<Admin>`.
 *
 * The full reload is intentional: the react-admin store may be what crashed, and
 * the Hub revoke is sent with `keepalive`, so it outlives the navigation.
 */
const SignOutButton = () => {
  const [pending, setPending] = useState(false);

  const signOut = () => {
    setPending(true);
    Promise.resolve()
      .then(() => authProvider.logout({}))
      .catch(() => undefined)
      .finally(() => window.location.assign('/login'));
  };

  return (
    <Button color='inherit' size='small' disabled={pending} onClick={signOut}>
      Sign out
    </Button>
  );
};

/**
 * Catches render-time crashes so a failure in one subtree does not white-screen
 * the whole dashboard.
 *
 * `showDialog` is deliberately off: Sentry's user-feedback dialog invites
 * free-text, which in this app would collect PHI.
 */
export const AppErrorBoundary: FC<AppErrorBoundaryProps> = ({ children, scope }) => (
  <Sentry.ErrorBoundary
    showDialog={false}
    fallback={({ resetError }) => (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px' padding={4}>
        <Alert severity='error' sx={{ maxWidth: 600 }}>
          <Typography variant='h6' gutterBottom>
            {scope ? `${scope} stopped responding` : 'Something went wrong'}
          </Typography>
          <Typography variant='body2' sx={{ mb: 2 }}>
            This has been reported. Try again, or sign out if the problem persists.
          </Typography>
          <Box display='flex' gap={1}>
            <Button variant='contained' size='small' onClick={resetError}>
              Try again
            </Button>
            <Button variant='outlined' size='small' onClick={() => window.location.reload()}>
              Reload
            </Button>
            <SignOutButton />
          </Box>
        </Alert>
      </Box>
    )}
  >
    {children}
  </Sentry.ErrorBoundary>
);

export default AppErrorBoundary;
